import React, { useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.js";
import {
  FileText,
  Search,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  X,
  Zap,
} from "lucide-react";

// Proper PDF Worker Setup
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const ResumeScanner = () => {
  const [file, setFile] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [jobDescription, setJobDescription] = useState("");

  // Extract PDF Text
  const extractPDFText = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const typedArray = new Uint8Array(arrayBuffer);

      const loadingTask = pdfjsLib.getDocument({ data: typedArray });
      const pdf = await loadingTask.promise;

      let fullText = "";

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();

        const pageText = textContent.items
          .map((item) => item.str)
          .join(" ");

        fullText += pageText + "\n";
      }

      return fullText;
    } catch (error) {
      console.error("PDF Extraction Error:", error);
      throw error;
    }
  };

  // File Upload
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResults(null);
    } else {
      alert("Please upload a valid PDF file.");
    }
  };

  // Resume Scan
  const handleScan = async () => {
    if (!file) return;

    setIsScanning(true);

    try {
      const extractedText = await extractPDFText(file);

      console.log("Extracted Resume Text:", extractedText);

      setTimeout(() => {
        setResults({
          atsScore: 78,
          jdMatch: jobDescription ? 64 : 55,
          summary:
            "Resume text extracted successfully. Your resume is well-structured, but adding more job-specific keywords can improve ATS score significantly.",
          missingKeywords: [
            "Docker",
            "Kubernetes",
            "CI/CD",
            "Cloud Computing",
          ],
          improvements: [
            {
              category: "EXPERIENCE",
              text:
                "Quantify achievements like 'Improved performance by 30%' for stronger impact.",
            },
            {
              category: "SKILLS",
              text:
                "Add more cloud and DevOps technologies to improve MNC shortlisting chances.",
            },
          ],
          extractedText,
        });

        setIsScanning(false);
      }, 1500);
    } catch (error) {
      console.error("Final Error:", error);
      setIsScanning(false);
      alert("Failed to extract text from PDF");
    }
  };

  return (
    <section className="py-20 bg-[#f8fafc] px-4 font-sans min-h-screen pb-32">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-serif text-[#0f172a] mb-6">
            AI Resume <span className="text-[#0ea5e9]">Scanner</span>
          </h1>
          <p className="text-slate-500 text-lg font-medium max-w-2xl mx-auto">
            Upload your resume PDF and check ATS compatibility instantly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* LEFT SIDE */}
          <div className="space-y-6">
            {/* Upload Card */}
            <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <FileText size={20} className="text-[#0ea5e9]" />
                <h3 className="text-xl font-serif">Upload Resume</h3>
              </div>

              <div className="relative border-2 border-dashed rounded-3xl p-6 border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-slate-700">
                      {file ? file.name : "No file selected"}
                    </p>
                    <p className="text-xs text-slate-400 uppercase font-bold">
                      PDF only
                    </p>
                  </div>

                  {file && (
                    <X
                      size={18}
                      className="cursor-pointer hover:text-red-500"
                      onClick={() => {
                        setFile(null);
                        setResults(null);
                      }}
                    />
                  )}
                </div>

                {!file && (
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                )}
              </div>
            </div>

            {/* Job Description */}
            <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Search size={20} className="text-[#0ea5e9]" />
                <h3 className="text-xl font-serif">Job Description</h3>
              </div>

              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste job description here..."
                className="w-full h-32 p-5 bg-slate-50 border rounded-2xl outline-none resize-none"
              />
            </div>

            {/* Scan Button */}
            <button
              onClick={handleScan}
              disabled={!file || isScanning}
              className={`w-full py-5 rounded-2xl font-bold flex items-center justify-center gap-3 text-white ${
                !file
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-[#0086c9] hover:bg-blue-600"
              }`}
            >
              {isScanning ? "Analyzing..." : "Scan Resume"}
              {!isScanning && <Zap size={20} />}
            </button>
          </div>

          {/* RIGHT SIDE */}
          <div>
            {results && (
              <div className="space-y-6">
                {/* ATS Score */}
                <div className="bg-white p-8 rounded-[2rem] shadow-sm">
                  <p className="text-4xl font-bold text-blue-500">
                    {results.atsScore}%
                  </p>
                  <p className="text-slate-500">ATS Score</p>
                </div>

                {/* Summary */}
                <div className="bg-white p-8 rounded-[2rem] shadow-sm">
                  <p className="font-bold mb-3">Analysis Summary</p>
                  <p>{results.summary}</p>
                </div>

                {/* Extracted Text */}
                <div className="bg-white p-8 rounded-[2rem] shadow-sm">
                  <p className="font-bold mb-3">Extracted Resume Text</p>
                  <div className="max-h-72 overflow-auto text-sm text-slate-600 whitespace-pre-wrap">
                    {results.extractedText}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResumeScanner;
